import React from 'react';
import { Icon } from './Icon';

/**
 * Trailing row actions for user/member lists: activate/deactivate + delete.
 * Delete is disabled while the account is active (must deactivate first) and
 * may be blocked with a reason (e.g. owner-of-a-slug). `hidden` renders
 * nothing — used for protected users (the superadmin).
 *
 * Icon-only controls (no shared `Button`) to preserve the compact list look.
 */
export interface UserRowActionsProps {
  isActive: boolean;
  onToggle: () => void;
  onDelete: () => void;
  /** When set, the toggle is disabled and this is shown as its tooltip (e.g. superadmin). */
  toggleDisabledReason?: string;
  /** When set, delete is disabled and this is shown as its tooltip. */
  deleteDisabledReason?: string;
  /** Disable both buttons while a mutation is in flight. */
  busy?: boolean;
  /** Protected user — render nothing at all. */
  hidden?: boolean;
  /** Tooltip for the deactivate action (when the user is active).
   *  Default Spanish ("Desactivar"). */
  deactivateLabel?: string;
  /** Tooltip for the activate action (when the user is inactive).
   *  Default Spanish ("Activar"). */
  activateLabel?: string;
  /** Tooltip on the disabled delete button while the user is still
   *  active. Default Spanish ("Desactiva primero al usuario"). */
  deleteBlockedLabel?: string;
  /** Tooltip for the delete action. Default Spanish ("Eliminar"). */
  deleteLabel?: string;
  /** Delete does not require the account to be inactive first. For actions
   *  that do NOT destroy the account (removing a membership from one
   *  workshop): the "deactivate first" guard only makes sense for the
   *  global delete. */
  deleteWhenActive?: boolean;
}

const BTN = 'w-9 h-9 flex items-center justify-center rounded-xl transition-all border shadow-sm';
const BTN_DISABLED = 'text-slate-300 dark:text-slate-600 border-border-subtle dark:border-border-subtle-dark opacity-40 cursor-not-allowed';

const UserRowActions: React.FC<UserRowActionsProps> = ({
  isActive,
  onToggle,
  onDelete,
  toggleDisabledReason,
  deleteDisabledReason,
  busy,
  hidden,
  deactivateLabel = 'Desactivar',
  activateLabel = 'Activar',
  deleteBlockedLabel = 'Desactiva primero al usuario',
  deleteLabel = 'Eliminar',
  deleteWhenActive = false,
}) => {
  if (hidden) return null;
  const toggleDisabled = !!busy || !!toggleDisabledReason;
  const deleteBlockedByActive = isActive && !deleteWhenActive;
  const deleteDisabled = deleteBlockedByActive || !!busy || !!deleteDisabledReason;
  return (
    <div className="flex items-center justify-end gap-1.5">
      {/* architecture-check-disable-next-line — icon-only list control */}
      <button
        type="button"
        title={toggleDisabledReason || (isActive ? deactivateLabel : activateLabel)}
        disabled={toggleDisabled}
        className={`${BTN} disabled:opacity-50 ${
          toggleDisabledReason
            ? BTN_DISABLED
            : isActive
              ? 'text-slate-400 border-border-subtle dark:border-border-subtle-dark hover:text-amber-600 hover:bg-amber-50 hover:border-amber-300 dark:hover:bg-amber-900/20'
              : 'text-emerald-500 border-emerald-200 dark:border-emerald-800 hover:text-white hover:bg-emerald-500 hover:border-emerald-500'
        }`}
        onClick={() => { if (!toggleDisabled) onToggle(); }}
      >
        <Icon name={isActive ? 'person_off' : 'person'} className="!text-[18px]" />
      </button>
      {/* architecture-check-disable-next-line — icon-only list control */}
      <button
        type="button"
        title={deleteDisabledReason || (deleteBlockedByActive ? deleteBlockedLabel : deleteLabel)}
        disabled={deleteDisabled}
        className={`${BTN} ${
          deleteDisabled
            ? 'text-slate-300 dark:text-slate-600 border-border-subtle dark:border-border-subtle-dark opacity-40 cursor-not-allowed'
            : 'text-rose-500 border-rose-300 dark:border-rose-800 hover:text-white hover:bg-rose-500 hover:border-rose-500'
        }`}
        onClick={() => { if (!deleteDisabled) onDelete(); }}
      >
        <Icon name="delete" className="!text-[18px]" />
      </button>
    </div>
  );
};

export default UserRowActions;
